import { useEffect, useRef, useState } from "react";
import { Cookie, X } from "lucide-react";
import { initTracking, setConsent } from "@/lib/tracking";
import { isNative } from "@/lib/native";

const KEY = "cc_consent";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (isNative()) return; // native app loads no trackers and shows no cookie banner
    const choice = localStorage.getItem(KEY);
    if (choice === "accepted") {
      setConsent(true);
      initTracking();
    } else if (!choice) {
      setVisible(true);
    }
    const reopen = () => setVisible(true);
    window.addEventListener("cc-open-consent", reopen);
    return () => window.removeEventListener("cc-open-consent", reopen);
  }, []);

  // While the banner is open, pad the page so it never covers content on any page.
  useEffect(() => {
    if (visible && ref.current) {
      const apply = () => {
        if (ref.current) document.body.style.paddingBottom = `${ref.current.offsetHeight + 24}px`;
      };
      apply();
      window.addEventListener("resize", apply);
      return () => {
        window.removeEventListener("resize", apply);
        document.body.style.paddingBottom = "";
      };
    }
    document.body.style.paddingBottom = "";
  }, [visible]);

  const accept = () => {
    localStorage.setItem(KEY, "accepted");
    setConsent(true);
    initTracking();
    setVisible(false);
  };

  // Dismiss (the X) declines non-essential tracking, same as Decline.
  const decline = () => {
    localStorage.setItem(KEY, "declined");
    setConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      ref={ref}
      data-testid="cookie-consent"
      className="fixed inset-x-3 bottom-3 z-[100] mx-auto max-w-[720px] rounded-2xl border border-white/10 bg-[#14213D]/95 p-5 pr-11 shadow-2xl backdrop-blur-md sm:inset-x-6 md:p-6 md:pr-12"
    >
      <button
        type="button"
        onClick={decline}
        data-testid="cookie-dismiss"
        aria-label="Dismiss and decline non-essential cookies"
        className="absolute right-2.5 top-2.5 inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9FB2C9] transition-colors hover:bg-white/10 hover:text-white"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Cookie className="mt-0.5 h-6 w-6 flex-shrink-0 text-[#F5A623]" />
          <p className="text-[13px] leading-relaxed text-[#BFD4E8]">
            We use cookies to measure traffic and improve the experience. Analytics
            (Meta, Google &amp; TikTok) load only if you accept. See how it works in our
            License &amp; FAQ.
          </p>
        </div>
        <div className="flex flex-shrink-0 gap-3">
          <button
            type="button"
            onClick={decline}
            data-testid="cookie-decline"
            className="rounded-xl border border-white/20 px-5 py-2.5 font-heading text-sm font-bold text-white transition-colors hover:bg-white/10"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={accept}
            data-testid="cookie-accept"
            className="rounded-xl bg-[#F5A623] px-5 py-2.5 font-heading text-sm font-extrabold text-[#0B132B] transition-transform duration-200 hover:-translate-y-0.5"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
